import { TextField } from '@execonline-inc/execonline-ui';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T } from '../../../../Translations';
import { capturedValue, CapturedAttribute, DiagnosedField } from '../../Types';

interface Props {
  diagnosed: DiagnosedField;
  required: boolean;
  captured: Maybe<ReadonlyArray<CapturedAttribute>>;
  claim: string;
  disabled: boolean;
  onClaimChange: (claim: string) => void;
}

const DiagnosticRow: React.FC<Props> = ({
  diagnosed,
  required,
  captured,
  claim,
  disabled,
  onClaimChange,
}) => {
  const value = captured.andThen((attributes) => capturedValue(diagnosed.field, attributes));

  return (
    <tr>
      <td className="border-b border-border py-3 pr-3 align-top text-sm font-medium text-foreground">
        <T kind={diagnosed.label} />
        <span className="mt-1 block">
          {required ? (
            <span className="rounded bg-secondary px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-primary">
              <T kind="Required" />
            </span>
          ) : (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              <T kind="Optional" />
            </span>
          )}
        </span>
      </td>
      <td className="border-b border-border py-3 pr-3 align-top">
        {captured
          .map(() =>
            value
              .map(() => (
                <span key="status" className="text-sm font-semibold text-accent">
                  &#10003; <T kind="Matched" />
                </span>
              ))
              .getOrElse(() => (
                <span
                  key="status"
                  className={`text-sm font-semibold ${required ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  &#10005; <T kind="Not sent" />
                </span>
              )),
          )
          .getOrElse(() => (
            <span key="status" className="text-sm font-semibold text-muted-foreground">
              <T kind="Not tested" />
            </span>
          ))}
      </td>
      <td className="border-b border-border py-3 align-top">
        {captured
          .map(() =>
            value
              .map((received) => (
                <span key="value" className="break-all font-mono text-xs text-foreground">
                  {received}
                </span>
              ))
              .getOrElse(() => (
                <span key="value" className="text-sm text-muted-foreground">
                  {diagnosed.field === 'custom:employee_number' ? (
                    <T kind="No matching claim received. Add employeeNumber in your IdP (recommended - it keeps accounts stable when email addresses change), or continue without it." />
                  ) : (
                    <T kind="No matching claim received. Add the claim in your IdP, or map a different claim below, then run another test." />
                  )}
                </span>
              )),
          )
          .getOrElse(() => (
            <span key="value" className="text-sm text-muted-foreground">
              &mdash;
            </span>
          ))}
        <div className="mt-2 max-w-xs">
          <TextField
            label={<T kind="SAML claim to map" />}
            labelProps={{ className: 'text-xs' }}
            value={claim}
            disabled={disabled}
            onChange={(event) => onClaimChange(event.target.value)}
            inputClassName="border-none font-mono text-xs"
            fullWidth
            data-intercom-target="sso-claim-mapping-input-field"
          />
        </div>
      </td>
    </tr>
  );
};

export default observer(DiagnosticRow);
